
import React, { useState } from 'react';

const FormsView: React.FC = () => {
  const [activeForm, setActiveForm] = useState<'none' | 'housing'>('none');
  const [page, setPage] = useState(1);

  if (activeForm === 'housing') {
    return (
      <div className="max-w-4xl mx-auto py-12 space-y-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end border-b-8 border-black pb-8 gap-4">
          <div className="space-y-2">
             <div className="inline-block bg-black px-3 py-1 text-white font-bold text-[10px] uppercase tracking-widest mb-2">Internal Housing Document</div>
             <h2 className="ymca-brand-font text-6xl tracking-tight leading-none uppercase">Housing Application Form</h2>
             <p className="text-gray-600 font-medium">Form ID: YMCA-HS-2024-001</p>
          </div>
          <div className="text-right">
             <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Application Progress</p>
             <p className="ymca-brand-font text-4xl">Step {page} of 4</p>
          </div>
        </div>

        <div className="bg-white p-2">
          {page === 1 ? (
            <div className="space-y-12">
              <div className="bg-ymca-neutral p-8 border-l-8 border-black">
                <h3 className="text-xl font-bold uppercase tracking-tight mb-2">Introduction & Project Selection</h3>
                <p className="text-gray-600 text-sm leading-relaxed max-w-2xl">
                   This form is the first step in applying for housing at One YMCA. Please provide accurate details to ensure we can offer the most appropriate support for your needs.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                <FormField label="Preferred Project Location *" type="select" options={['Select project...', 'Hatfield Housing', 'Watford Residential', 'Stevenage Outreach', 'North Herts Hostel']} />
                <FormField label="Application Date *" type="text" placeholder="DD/MM/YYYY" />
                
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-gray-100">
                  <FormField label="Salutation" type="select" options={['Mr', 'Mrs', 'Ms', 'Mx', 'Dr', 'Prefer not to say']} />
                  <FormField label="First Name(s) *" type="text" placeholder="Enter first name" />
                  <FormField label="Last Name *" type="text" placeholder="Enter last name" />
                </div>

                <div className="md:col-span-2 pt-6 border-t border-gray-100">
                   <h4 className="ymca-brand-font text-3xl mb-6 uppercase">Contact Information</h4>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <FormField label="Primary Contact Number *" type="text" placeholder="07XXX XXXXXX" />
                      <FormField label="Email Address *" type="email" placeholder="email@example.com" />
                   </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-12 animate-in fade-in duration-300">
               <div className="bg-ymca-neutral p-8 border-l-8 border-black">
                <h3 className="text-xl font-bold uppercase tracking-tight mb-2">Personal & Background Details</h3>
                <p className="text-gray-600 text-sm">Required for assessment and safeguarding protocols.</p>
              </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                  <FormField label="Date of Birth *" type="text" placeholder="DD/MM/YYYY" />
                  <FormField label="National Insurance Number" type="text" placeholder="QQ 12 34 56 C" />
                  <FormField label="Legal Gender" type="select" options={['Male', 'Female', 'Other', 'Prefer not to say']} />
                  <FormField label="Gender Identity" type="text" placeholder="Enter identity if different" />
                  <FormField label="Immigration Status *" type="select" options={['British Citizen', 'Indefinite Leave to Remain', 'Asylum Seeker', 'Other']} />
                  <FormField label="Local Connection Area" type="select" options={['Hertfordshire', 'Bedfordshire', 'Buckinghamshire', 'Other']} />
               </div>

               <div className="pt-10 border-t-2 border-black">
                  <h4 className="ymca-brand-font text-3xl mb-8 uppercase">Support Needs Assessment</h4>
                  <div className="grid grid-cols-1 gap-10">
                     <FormField label="Primary Support Requirement *" type="select" options={['Mental Health', 'Complex Needs', 'Youth Homelessness', 'Domestic Abuse Recovery', 'Drug/Alcohol Support']} />
                     <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 block mb-3">Additional Details on Support Needs</label>
                        <textarea className="form-input" rows={4} placeholder="Please provide any relevant details for our assessment team..."></textarea>
                     </div>
                  </div>
               </div>
            </div>
          )}

          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mt-16 pt-10 border-t-4 border-black">
            <button 
              onClick={() => page === 1 ? setActiveForm('none') : setPage(p => p - 1)}
              className="w-full md:w-auto px-12 py-5 border-2 border-black font-bold text-xs tracking-widest uppercase hover:bg-gray-100 transition-all"
            >
              {page === 1 ? 'Discard & Return' : 'Back to previous step'}
            </button>
            <button 
              onClick={() => page < 4 ? setPage(p => p + 1) : setActiveForm('none')}
              className="w-full md:w-auto px-16 py-5 bg-black text-white font-bold text-xs tracking-widest uppercase hover:bg-ymca-orange transition-all"
            >
              {page === 4 ? 'Complete Submission' : 'Continue to next step'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div className="section-header flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div className="space-y-1">
          <h2 className="ymca-brand-font text-6xl uppercase leading-none">Operational Documents</h2>
          <p className="text-gray-500 font-medium">Manage charitable workflow forms and residential applications.</p>
        </div>
        <button className="bg-black text-white px-8 py-4 font-bold text-xs uppercase tracking-widest hover:bg-ymca-orange transition-all">
          Build New Document
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <DocumentCard 
          title="Housing Application" 
          id="HS-2024-001"
          description="Standard residential intake form for One YMCA housing services." 
          status="Active" 
          submissions={242}
          onClick={() => setActiveForm('housing')}
        />
        <DocumentCard 
          title="Incident Reporting" 
          id="HS-2024-002"
          description="Internal record for residential or outreach incidents and health & safety." 
          status="Active" 
          submissions={48}
        />
        <DocumentCard 
          title="Staff Onboarding" 
          id="HR-2024-009"
          description="Employee registration, bank details, and compliance checklist." 
          status="Draft" 
          submissions={12}
        />
      </div>
    </div>
  );
};

const DocumentCard = ({ title, id, description, status, submissions, onClick }: any) => (
  <div className="border-4 border-black p-10 hover:shadow-[12px_12px_0px_0px_rgba(243,112,33,1)] transition-all cursor-default group">
    <div className="flex justify-between items-start mb-8">
      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{id}</div>
      <span className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1 border-2 border-black ${status === 'Draft' ? 'bg-gray-100' : 'bg-black text-white'}`}>
        {status}
      </span>
    </div>
    <h3 className="ymca-brand-font text-4xl leading-none mb-4 uppercase">{title}</h3>
    <p className="text-sm font-medium text-gray-500 mb-10 h-10 overflow-hidden leading-relaxed">{description}</p>
    
    <div className="flex items-end justify-between pt-8 border-t-2 border-gray-100">
       <div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Volume</p>
          <p className="ymca-brand-font text-4xl">{submissions}</p>
       </div>
       <button 
         onClick={onClick}
         className="px-6 py-3 bg-black text-white font-bold text-[10px] tracking-widest uppercase hover:bg-ymca-orange transition-all"
       >
         Open Portal
       </button>
    </div>
  </div>
);

const FormField = ({ label, type, placeholder, options }: any) => (
  <div className="space-y-3">
    <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 block">
      {label}
    </label>
    {type === 'select' ? (
      <select className="form-input font-bold appearance-none bg-white">
        {options.map((opt: string) => <option key={opt}>{opt}</option>)}
      </select>
    ) : (
      <input 
        type={type} 
        placeholder={placeholder} 
        className="form-input focus:border-ymca-orange transition-all" 
      />
    )}
  </div>
);

export default FormsView;
